import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { FaPlus, FaTrash, FaEdit, FaPrint, FaShareAlt, FaCopy, FaWhatsapp, FaEnvelope, FaExternalLinkAlt, FaFilePdf } from 'react-icons/fa';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import FichasHeader from '../components/FichasHeader';

const PageStyle = createGlobalStyle`
  @media print { body { display: none; } }
`;

export default function FichasLista() {
  const navigate        = useNavigate();
  useAuth(); // mantiene el contexto activo
  const [fichas, setFichas]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [shareModal, setShareModal] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    api.getFichas()
      .then(data => setFichas(data || []))
      .catch(() => setFichas([]))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta ficha?')) return;
    await api.deleteFicha(id).catch(() => {});
    setFichas(prev => prev.filter(f => f.id !== id));
  };

  const openShare = (f) => { setShareModal(f); setLinkCopied(false); };

  const shareUrl = (ficha) => `${window.location.origin}/fichas/ver/${ficha.id}`;

  const handleCopyLink = (ficha) => {
    navigator.clipboard?.writeText(shareUrl(ficha)).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    }).catch(() => {});
  };

  const handleWhatsApp = (ficha) => {
    const nombre = ficha.nombre || ficha.form?.cliente || 'Ficha técnica';
    const text = `Ficha técnica — ${nombre}\n${shareUrl(ficha)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };

  const handleEmail = (ficha) => {
    const nombre = ficha.nombre || ficha.form?.cliente || 'Ficha técnica';
    const subject = encodeURIComponent(`Ficha técnica — ${nombre}`);
    const body = encodeURIComponent(`Hola,\n\nTe comparto la ficha técnica de Grupo Jaycom:\n\n${shareUrl(ficha)}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleOpenLink = (ficha) => {
    window.open(shareUrl(ficha), '_blank', 'noopener');
  };

  const handleSharePDF = (ficha) => {
    window.open(`${shareUrl(ficha)}?print=1`, '_blank', 'noopener');
    setShareModal(null);
  };

  return (
    <>
      <PageStyle />
      <Page>
        <FichasHeader />

        <Content>
          {loading ? (
            <Empty><p>Cargando fichas…</p></Empty>
          ) : fichas.length === 0 ? (
            <Empty>
              <EmptyIcon>📋</EmptyIcon>
              <p>No hay fichas guardadas aún.</p>
              <NewBtn $big onClick={() => navigate('/fichas/nueva')}>
                <FaPlus size={14} /> Crear primera ficha
              </NewBtn>
            </Empty>
          ) : (
            <Grid>
              {fichas.map(f => (
                <Card key={f.id}>
                  {f.snapshot && (
                    <CardImg src={f.snapshot} alt="Vista 3D" />
                  )}
                  <CardBody>
                    {f.nombre && <CardNombre>{f.nombre}</CardNombre>}
                    <CardTipo $polin={f.tipo === 'polin'}>
                      {f.tipo === 'polin' ? 'Polín / Rifle' : 'Tacón'}
                    </CardTipo>
                    <CardCliente>{f.form?.cliente || <em>Sin cliente</em>}</CardCliente>
                    <CardMeta>
                      <span>{f.form?.fecha || '—'}</span>
                      {f.tipo === 'polin'
                        ? f.polinNiveles?.larguero?.largo && (
                            <span>
                              {f.polinNiveles.larguero.largo} {f.polinUnits?.larguero?.largo} ×{' '}
                              {f.polinNiveles.superior?.largo} {f.polinUnits?.superior?.largo}
                            </span>
                          )
                        : f.form?.superior?.largo && (
                            <span>
                              {f.form.superior.largo} {f.units?.superior?.largo} ×{' '}
                              {f.form.medio?.largo} {f.units?.medio?.largo}
                            </span>
                          )
                      }
                    </CardMeta>
                    <CardNiveles>
                      {f.tipo === 'polin'
                        ? [
                            { k: 'superior', label: 'Sup.' },
                            { k: 'larguero', label: 'Larg.' },
                            { k: 'inferior', label: 'Inf.' },
                          ].map(({ k, label }) => {
                            const n = f.polinNiveles?.[k];
                            if (!n?.largo && !n?.qty) return null;
                            return (
                              <NivelChip key={k}>
                                <b>{label}</b> {n.largo && `${n.largo}″`} {n.qty && `× ${n.qty}`}
                              </NivelChip>
                            );
                          })
                        : [
                            { k: 'superior', label: 'Sup.' },
                            { k: 'medio',    label: 'Med.' },
                            { k: 'tacon',    label: 'Tac.' },
                            { k: 'inferior', label: 'Inf.' },
                          ].map(({ k, label }) => {
                            const n = f.form?.[k];
                            if (!n?.largo && !n?.qty) return null;
                            return (
                              <NivelChip key={k}>
                                <b>{label}</b> {n.largo && `${n.largo}″`} {n.qty && `× ${n.qty}`}
                              </NivelChip>
                            );
                          })
                      }
                    </CardNiveles>
                  </CardBody>
                  <CardActions>
                    <IconBtn title="Editar" onClick={() => navigate(`/fichas/editar/${f.id}`)}>
                      <FaEdit />
                    </IconBtn>
                    <IconBtn title="Imprimir" onClick={() => navigate(`/fichas/editar/${f.id}?print=1`)}>
                      <FaPrint />
                    </IconBtn>
                    <IconBtn title="Compartir" onClick={() => openShare(f)}>
                      <FaShareAlt />
                    </IconBtn>
                    <IconBtn $danger title="Eliminar" onClick={() => handleDelete(f.id)}>
                      <FaTrash />
                    </IconBtn>
                  </CardActions>
                </Card>
              ))}
            </Grid>
          )}
        </Content>

        {shareModal && (
          <ShareOverlay onClick={() => setShareModal(null)}>
            <ShareBox onClick={e => e.stopPropagation()}>
              <ShareBoxHeader>
                <ShareBoxTitle>Compartir ficha</ShareBoxTitle>
                <CloseBtn onClick={() => setShareModal(null)} aria-label="Cerrar">✕</CloseBtn>
              </ShareBoxHeader>
              {shareModal.nombre && <ShareNombre>{shareModal.nombre}</ShareNombre>}
              <ShareClient>{shareModal.form?.cliente || <em>Sin cliente</em>}</ShareClient>
              <ShareActions>
                <ShareBtn $copy onClick={() => handleCopyLink(shareModal)}>
                  <FaCopy size={13} />
                  {linkCopied ? '¡Link copiado!' : 'Copiar link'}
                </ShareBtn>
                <ShareBtn $wa onClick={() => handleWhatsApp(shareModal)}>
                  <FaWhatsapp size={15} />
                  Compartir por WhatsApp
                </ShareBtn>
                <ShareBtn $mail onClick={() => handleEmail(shareModal)}>
                  <FaEnvelope size={13} />
                  Compartir por correo
                </ShareBtn>
                <ShareBtn $open onClick={() => handleOpenLink(shareModal)}>
                  <FaExternalLinkAlt size={12} />
                  Abrir link
                </ShareBtn>
                <ShareDivider />
                <ShareBtn $pdf onClick={() => handleSharePDF(shareModal)}>
                  <FaFilePdf size={14} />
                  Compartir PDF
                </ShareBtn>
              </ShareActions>
            </ShareBox>
          </ShareOverlay>
        )}
      </Page>
    </>
  );
}

/* ── Estilos ── */
const Page = styled.div`
  min-height: 100vh;
  background: var(--mid);
  padding-bottom: 3rem;
`;

const NewBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--blue);
  color: white;
  border: none;
  border-radius: 4px;
  padding: ${p => p.$big ? '0.65rem 1.4rem' : '0.5rem 1.1rem'};
  font-size: ${p => p.$big ? '0.95rem' : '0.85rem'};
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: var(--blue-dk); }
`;

const Content = styled.main`
  max-width: 1100px;
  margin: 2rem auto;
  padding: 0 1.5rem;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 4rem 2rem;
  color: var(--gray);
  font-size: 1rem;
`;

const EmptyIcon = styled.div`font-size: 3rem;`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.25rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 6px;
  border: 1px solid var(--border);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.15s;
  &:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.12); }
`;

const CardImg = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-bottom: 1px solid var(--border);
  background: #f5f0ea;
`;

const CardBody = styled.div`
  padding: 0.85rem 1rem 0.5rem;
  flex: 1;
`;

const CardNombre = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--blue);
  margin-bottom: 0.2rem;
`;

const CardTipo = styled.div`
  display: inline-block;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${p => p.$polin ? 'var(--red)' : 'var(--blue)'};
  border: 1px solid ${p => p.$polin ? 'var(--red)' : 'var(--blue)'};
  border-radius: 3px;
  padding: 1px 6px;
  margin-bottom: 0.35rem;
`;

const CardCliente = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 0.3rem;
  em { color: var(--gray); font-style: italic; font-weight: 400; }
`;

const CardMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-size: 0.78rem;
  color: var(--gray);
  margin-bottom: 0.5rem;
`;

const CardNiveles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
`;

const NivelChip = styled.span`
  background: var(--blue-lt);
  color: var(--blue);
  border-radius: 3px;
  padding: 2px 7px;
  font-size: 0.72rem;
`;

const CardActions = styled.div`
  display: flex;
  border-top: 1px solid var(--border);
`;

const IconBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem;
  border: none;
  background: none;
  color: ${p => p.$danger ? 'var(--red)' : 'var(--blue)'};
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.12s;
  &:hover { background: ${p => p.$danger ? 'var(--red-lt)' : 'var(--blue-lt)'}; }
  & + & { border-left: 1px solid var(--border); }
`;

/* ── Share modal ── */
const ShareOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ShareBox = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  width: 100%;
  max-width: 360px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
`;

const ShareBoxHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
`;

const ShareBoxTitle = styled.h2`
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.2rem;
  color: var(--blue);
  letter-spacing: 0.04em;
`;

const CloseBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1rem;
  color: var(--gray);
  line-height: 1;
  padding: 0.2rem 0.3rem;
  &:hover { color: var(--text); }
`;

const ShareNombre = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--blue);
  margin-bottom: 0.1rem;
`;

const ShareClient = styled.p`
  font-size: 0.88rem;
  color: var(--text-sub);
  margin-bottom: 1.25rem;
  em { font-style: italic; }
`;

const ShareActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const SHARE_COLORS = {
  $copy: 'var(--blue)',
  $wa:   '#25D366',
  $mail: '#EA4335',
  $open: '#555',
  $pdf:  'var(--red)',
};

const ShareBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.65rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s, filter 0.15s;
  color: white;
  background: ${p =>
    p.$copy ? SHARE_COLORS.$copy :
    p.$wa   ? SHARE_COLORS.$wa   :
    p.$mail ? SHARE_COLORS.$mail :
    p.$open ? SHARE_COLORS.$open :
              SHARE_COLORS.$pdf};
  &:hover { filter: brightness(0.9); }
`;

const ShareDivider = styled.hr`
  border: none;
  border-top: 1px solid var(--border);
  margin: 0.25rem 0;
`;
